import { Fragment, useState } from "react";
import { useInView } from "react-intersection-observer";
import { ArticleDataObject, seeAlso, Tag } from "@app/types/Cue";
import { AdvertisementTypeEnum } from "@app/types/enums";
import Advertisement from "@components/Advertisement/Advertisement";
import PrestitialAdWrapper from "@components/Advertisement/PrestitialAdWrapper";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import Container from "@components/Container/Container";
import FooterSubscribeRow from "@components/Footer/FooterSubscribeRow/FooterSubscribeRow";
import GAData from "@components/GAData/GAData";
import {
  getArticleAuthorNames,
  getArticleChapter1,
  getArticleKeywords,
  getArticleLevel2,
} from "@components/GAData/helper";
import Header from "@components/Header/Header";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useArticleGiftReceived from "@hooks/useArticleGiftReceived";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { getFormattedTextForAds } from "@pages/Section/helpers";
import {
  ARTICLE_INIFINITE_SCROLL_LIMIT,
  sectionNavigationItems,
} from "@util/constant";
import {
  GoogleAdsSlotFactory,
  isPathAVerticalPath,
  toUnderscoreFromSpace,
} from "@util/helpers";
import axios from "axios";

import ArticleSubshareModal from "./components/ArticleSubshare/ArticleSubshareModal";
import ArticleSubsharePrompt from "./components/ArticleSubshare/ArticleSubsharePrompt";
import ArticleSubshareReceived from "./components/ArticleSubshare/ArticleSubshareReceived";
import { LAMBDA_RESPONSE_TYPES } from "./types/Alacrity";
import { ArticleFactory, VerticalHeaderFactory } from "./utils/helpers";

type ArticleContentProps = {
  context: ArticleDataObject;
  breakingNews: ArticleDataObject[];
  sectionNews: ArticleDataObject[];
  seeAlso: seeAlso;
};

export default function ArticleContent({
  context,
  breakingNews,
  sectionNews,
  seeAlso,
}: ArticleContentProps): React.ReactElement {
  const [moreStories, setMoreStories] = useState<ArticleDataObject[]>([
    context,
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [showGiftSubscribePrompt, setShowGiftSubscribePrompt] = useState(false);

  const { isPrestitialEnabled, topOverlayImpressions, topOverlayValidity } =
    useGlobalAdSetting({
      targeting: [{ key: "page", value: "article" }],
    });
  const { isModalOpen, setIsModalOpen, alacrityRes, isGiftReceived } =
    useArticleGiftReceived();

  // Article data de-construct
  const { sections } = context;

  const mainSection = sections?.at(0);
  const uniqueName = mainSection?.uniqueName;
  const uniqueNameAds = uniqueName?.replace("_", "/") || "";
  const verticalPath = "/" + uniqueNameAds;
  const parentCategory = uniqueName?.split("_")[0];
  const childCategory = uniqueName?.split("_")[1];
  const isThisArticlegifted =
    alacrityRes?.data.responseType ===
    LAMBDA_RESPONSE_TYPES.SUCCESS_VALID_GIFT_TOKEN;

  const VerticalHeaderLayout = VerticalHeaderFactory({ verticalPath });

  const fetchTheNextArticle = async () => {
    setIsLoading(true);
    const lastArticle = moreStories?.at(-1);
    if (!lastArticle) return;
    console.log("[START] Fetching next article..."); // eslint-disable-line no-console
    console.log({ moreStories, lastArticle }); // eslint-disable-line no-console

    try {
      const response = await axios.post("/_plat/api/v1/next-article", {
        category: lastArticle?.sections?.[0].uniqueName, // "companies-markets_banking-finance" || "food-drink"
        sort: lastArticle?.sort, // [1703168467000, "2166527"]
      });
      const nextStory = response.data;
      setMoreStories([...moreStories, nextStory]);
    } catch (error) {
      return [];
    } finally {
      setIsLoading(false);
    }

    console.log("[END] Fetching next article..."); // eslint-disable-line no-console
  };

  const { ref } = useInView({
    triggerOnce: true,
    onChange: (inView) => {
      if (
        inView &&
        moreStories.length < ARTICLE_INIFINITE_SCROLL_LIMIT &&
        true // isLocalDevOrStg CONSTANT used in bt-web
      )
        fetchTheNextArticle();
    },
  });

  const { ref: showGiftSubscribePromptRef } = useInView({
    triggerOnce: true,
    onChange: (inView) => {
      if (inView && isThisArticlegifted) setShowGiftSubscribePrompt(true);
    },
  });

  return (
    <div className="w-full" data-testid="article-content-component">
      {!isPathAVerticalPath(verticalPath) ? (
        <Advertisement
          rootClassName="bg-stripes py-3"
          adUnitProps={{
            type: AdvertisementTypeEnum.LB1,
            slot: GoogleAdsSlotFactory.lb1(uniqueNameAds),
            slotTargettings: [
              { key: "btarticleid", value: context?.id },
              {
                key: "bttags",
                value:
                  context?.tags
                    ?.map((tag: Tag) => getFormattedTextForAds(tag.name))
                    .join(", ") || "",
              },
            ],
          }}
        />
      ) : null}

      <PrestitialAdWrapper
        slot={GoogleAdsSlotFactory.prestitial(uniqueNameAds)}
        {...{ isPrestitialEnabled, topOverlayImpressions, topOverlayValidity }}
      />

      <Header mastheadTitle={uniqueName} />

      {parentCategory ? (
        <div className="w-full flex-none bg-gray-250">
          <Container>
            <Breadcrumb
              rootClassName="m-auto"
              parentCategory={parentCategory}
              childCategory={childCategory}
              categoryDataMapping={sectionNavigationItems}
            />
          </Container>
        </div>
      ) : null}

      <>
        {VerticalHeaderLayout ? <VerticalHeaderLayout /> : null}

        <div id="article-content-wrapper" className="w-full">
          {moreStories.map((article, index) => {
            // Check if article is undefined
            if (!article) {
              return null;
            }

            const displayType = article.displaySetting?.displayType;
            const mainSection = article.sections?.at(0);
            const ArticleLayout = ArticleFactory({ displayType });
            const isPremium = article.paywall?.contentAccess === "1";

            if (!displayType || !ArticleLayout) {
              return null;
            }

            return (
              <Fragment key={article.id}>
                <>
                  {mainSection ? (
                    <GAData
                      articleid={article.id}
                      author={getArticleAuthorNames(article.authors)}
                      chapter1={getArticleChapter1(mainSection)}
                      contentcat={isPremium ? 2 : 1}
                      contenttype="1"
                      keyword={getArticleKeywords(article.tags || [])}
                      level2={getArticleLevel2(mainSection)}
                      pubdate={article.updated}
                      title={toUnderscoreFromSpace(article.title)}
                    />
                  ) : null}
                </>

                <ArticleLayout
                  article={article}
                  isGiftReceived={isGiftReceived}
                  breakingNews={breakingNews}
                  sectionNews={sectionNews}
                  variant="default"
                  index={index}
                  seeAlso={seeAlso}
                />

                <div ref={ref}></div>
              </Fragment>
            );
          })}
        </div>

        <ArticleSubsharePrompt
          isModalOpen={showGiftSubscribePrompt}
          setIsModalOpen={setShowGiftSubscribePrompt}
        />

        <div
          aria-roledescription="article-subshare-prompt-observer"
          ref={showGiftSubscribePromptRef}
        ></div>

        {isLoading ? (
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} size="5x" spin className="mb-6" />
          </div>
        ) : null}

        <FooterSubscribeRow />

        <>
          {alacrityRes ? (
            <ArticleSubshareModal
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
            >
              <ArticleSubshareReceived
                alacrityRes={alacrityRes}
                setIsModalOpen={setIsModalOpen}
              />
            </ArticleSubshareModal>
          ) : null}
        </>
      </>
    </div>
  );
}
